import mixpanel from 'mixpanel-browser';

/**
 * Analytics is enabled in production or when REACT_APP_ENABLE_ANALYTICS=true
 * is set explicitly (useful for staging / manual QA without a prod build).
 */
const isEnabled = process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENABLE_ANALYTICS === 'true';

const isDev = process.env.NODE_ENV !== 'production';

/** Module-level store so track() can auto-attach user_email to every event. */
let _userEmail = '';
/** Module-level store so track() can auto-attach user_name to every event. */
let _userName = '';

// ─── Initialisation ──────────────────────────────────────────────────────────

export const initMixpanel = (): void => {
  if (!isEnabled) return;
  const token = process.env.REACT_APP_MIXPANEL_TOKEN;
  if (!token) {
    console.warn('[Analytics] REACT_APP_MIXPANEL_TOKEN is not set — tracking disabled.');
    return;
  }
  try {
    mixpanel.init(token, {
      debug: false,
      track_pageview: false, // SPA — we fire page events manually where needed
      persistence: 'localStorage',
    });
  } catch (e) {
    console.warn('[Analytics] init failed', e);
  }
};

// ─── Structured event names ───────────────────────────────────────────────────

export const ANALYTICS_EVENTS = {
  // Project / report navigation
  OPEN_PROJECT: 'open_project',
  OPEN_REPORT: 'open_report',

  // Report export
  EXPORT_REPORT: 'export_report',
  EXPORT_PHOTOS: 'export_photos',

  // PayApp workflow
  PAYAPP_OPEN: 'payapp_open',
  PAYAPP_CONFIRM: 'payapp_confirm',
  PAYAPP_SAVE_DRAFT: 'payapp_save_draft',
  PAYAPP_LINE_ITEM_MERGED: 'payapp_line_item_merged',
  PAYAPP_LINE_ITEM_NOT_MERGED: 'payapp_line_item_not_merged',

  // Generic task lifecycle (enables Mixpanel time-on-task via time_event)
  TASK_STARTED: 'task_started',
  TASK_COMPLETED: 'task_completed',
  TASK_ABANDONED: 'task_abandoned',
} as const;

export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

// ─── Event property shape (all optional so callers only pass what they have) ──

export interface TrackProperties {
  project_id?: string;
  project_label?: string; // "${projectNumber} - ${projectName}"
  project_type?: string;
  bank_name?: string;
  construction_type?: string;
  /** Required for task_* events */
  task_type?: string;
  report_type?: string;
  draw_name?: string;
  payapp_session_id?: string;
  merge_type?: 'system_exact' | 'system_auto' | 'manual';
  fuzzy_match_type?: string;
  payapp_description?: string;
  payapp_revised_budget?: number;
  payapp_line_item_id?: string;
  project_description?: string;
  project_revised_budget?: number;
  project_line_item_id?: string;
  fuzzy_score?: number;
  budget_match?: boolean;
  [key: string]: unknown;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export const Analytics = {
  /**
   * Call once after a successful login or session restore.
   * `email` is optional on session restore — Mixpanel already has it from
   * the login call (stored server-side under the identified user).
   */
  identify(userId: string, email?: string, name?: string): void {
    if (!isEnabled) return;
    try {
      mixpanel.identify(userId);
      if (email) {
        _userEmail = email;
        mixpanel.people.set({ $email: email });
      }
      if (name) {
        _userName = name;
      }
      if (isDev) console.debug('[Analytics] identify', { userId, email, name });
    } catch (e) {
      console.warn('[Analytics] identify failed', e);
    }
  },

  /**
   * Track a named event.  `user_email` and `timestamp` are attached
   * automatically so callers never need to pass them.
   */
  track(eventName: AnalyticsEvent | string, properties?: TrackProperties): void {
    if (!isEnabled) return;
    try {
      const payload = {
        ...(_userEmail && { user_email: _userEmail }),
        ...(_userName && { user_name: _userName }),
        ...properties,
        timestamp: new Date().toISOString(),
      };
      mixpanel.track(eventName, payload);
      if (isDev) console.debug('[Analytics] track', eventName, payload);
    } catch (e) {
      console.warn('[Analytics] track failed', e);
    }
  },

  /**
   * Start Mixpanel's built-in timer for `eventName`.
   * When `track(eventName)` is eventually called, Mixpanel automatically
   * adds a `$duration` property (seconds) to that event.
   *
   * Usage pattern:
   *   Analytics.timeEvent(ANALYTICS_EVENTS.TASK_COMPLETED);
   *   Analytics.track(ANALYTICS_EVENTS.TASK_STARTED, { task_type: 'payapp_confirm', ... });
   *   // ... user completes the workflow ...
   *   Analytics.track(ANALYTICS_EVENTS.TASK_COMPLETED, { task_type: 'payapp_confirm', ... });
   *   // ^ Mixpanel appends $duration automatically
   */
  timeEvent(eventName: AnalyticsEvent | string): void {
    if (!isEnabled) return;
    try {
      mixpanel.time_event(eventName);
    } catch (e) {
      console.warn('[Analytics] timeEvent failed', e);
    }
  },

  /**
   * Call on logout to disassociate the current user from future events.
   */
  reset(): void {
    if (!isEnabled) return;
    try {
      _userEmail = '';
      _userName = '';
      mixpanel.reset();
    } catch (e) {
      console.warn('[Analytics] reset failed', e);
    }
  },
};
